"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { supabase } from "@/lib/supabase";
import type { DbUserSearchUser } from "@/types/db";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

type OwnedAgentRow = {
  id: string;
  name: string;
  nickname: string | null;
  archived_at: string | null;
  updated_at: string | null;
};

const candidateLabel = (user: DbUserSearchUser) =>
  user.display_name?.trim() || user.username?.trim() || user.email?.trim() || user.user_id.slice(0, 8);

/**
 * Admin hand-over of everything a user owns, archived agents included.
 * Deactivation preserves these agents; shared agents should be handed over
 * so an active owner can maintain them.
 */
export function UserAgentsSection({ userId, userName }: { userId: string; userName: string }) {
  const [agents, setAgents] = useState<OwnedAgentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [candidates, setCandidates] = useState<DbUserSearchUser[]>([]);
  const [candidateId, setCandidateId] = useState("");
  const [searching, setSearching] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [transferring, setTransferring] = useState(false);

  const loadAgents = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc("list_user_owned_agents", { p_user_id: userId });
    if (error) {
      console.error("[UserAgentsSection] list owned agents failed:", error);
      toast.error(error.message || "Failed to load agents.");
      setAgents([]);
    } else {
      setAgents((data ?? []) as OwnedAgentRow[]);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadAgents();
  }, [loadAgents]);

  useEffect(() => {
    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      setSearching(true);

      let query = supabase
        .from("user_search_view")
        .select("user_id,username,display_name,email")
        .limit(20);

      const normalizedQuery = search.trim();
      if (normalizedQuery) {
        const safeQuery = normalizedQuery.replace(/,/g, " ");
        query = query.or(
          `display_name.ilike.%${safeQuery}%,username.ilike.%${safeQuery}%,email.ilike.%${safeQuery}%`
        );
      }

      const { data, error } = await query;
      if (cancelled) return;

      if (error) {
        console.error("[UserAgentsSection] new owner search failed:", error);
        setCandidates([]);
        setCandidateId("");
        setSearching(false);
        return;
      }

      const filtered = ((data as DbUserSearchUser[] | null) ?? []).filter(
        (candidate) => candidate.user_id !== userId
      );
      setCandidates(filtered);
      setCandidateId((previous) =>
        previous && filtered.some((candidate) => candidate.user_id === previous)
          ? previous
          : (filtered[0]?.user_id ?? "")
      );
      setSearching(false);
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [search, userId]);

  const selectedCandidate = candidates.find((candidate) => candidate.user_id === candidateId) ?? null;

  const handleTransferAgents = useCallback(async () => {
    if (!candidateId || transferring) return;
    setTransferring(true);
    const { error } = await supabase.rpc("transfer_user_agents", {
      p_from_user_id: userId,
      p_to_user_id: candidateId,
    });
    setTransferring(false);
    if (error) {
      console.error("[UserAgentsSection] transfer agents failed:", error);
      toast.error(error.message || "Failed to transfer agents.");
      return;
    }
    toast.success("Agents transferred.");
    setConfirmOpen(false);
    await loadAgents();
  }, [candidateId, loadAgents, transferring, userId]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Agents</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Agents this user owns. Deactivation preserves them. Transfer shared agents to an active owner as part of offboarding.
        </p>
        <Separator className="mt-4" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Spinner className="size-6 text-muted-foreground" />
        </div>
      ) : agents.length === 0 ? (
        <p className="text-sm text-muted-foreground">This user doesn&apos;t own any agents.</p>
      ) : (
        <>
          <div className="space-y-1.5">
            {agents.map((agent) => (
              <div
                key={agent.id}
                className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">{agent.name}</p>
                  {agent.nickname && <p className="truncate text-xs text-muted-foreground">@{agent.nickname}</p>}
                </div>
                {agent.archived_at && <Badge variant="outline" className="shrink-0 text-xs">Archived</Badge>}
              </div>
            ))}
          </div>

          <Separator />

          <div className="space-y-4">
            <div>
              <h3 className="text-base font-semibold text-foreground">Transfer all agents</h3>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Moves every agent above, with its team links, tools and files, to a new owner. Agents in this user&apos;s personal space move to the new owner&apos;s.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="agent-transfer-search">New owner</Label>
              <Input
                id="agent-transfer-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by name, username or email"
                maxLength={200}
              />
              <Select value={candidateId} onValueChange={setCandidateId} disabled={candidates.length === 0}>
                <SelectTrigger>
                  <SelectValue placeholder={searching ? "Searching…" : "No matching users"} />
                </SelectTrigger>
                <SelectContent>
                  {candidates.map((candidate) => (
                    <SelectItem key={candidate.user_id} value={candidate.user_id}>
                      {candidateLabel(candidate)}
                      {candidate.email ? ` (${candidate.email})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              type="button"
              size="sm"
              disabled={!candidateId || transferring}
              onClick={() => setConfirmOpen(true)}
            >
              {transferring && <Spinner className="size-3.5" />}
              {transferring ? "Transferring…" : `Transfer ${agents.length} ${agents.length === 1 ? "agent" : "agents"}`}
            </Button>
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!transferring) setConfirmOpen(open);
        }}
        title={`Transfer ${userName}'s agents?`}
        description={`All ${agents.length} ${agents.length === 1 ? "agent" : "agents"} will be owned by ${selectedCandidate ? candidateLabel(selectedCandidate) : "the selected user"}.`}
        confirmLabel="Transfer"
        variant="default"
        loading={transferring}
        onConfirm={() => void handleTransferAgents()}
      />
    </div>
  );
}
